import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MessageCircle, Send, CheckCircle, Users, Sparkles, ArrowRight, MessageSquare, 
  Wand2, Clock, User, Mail, Search, RefreshCw, AlertCircle, X, StickyNote, Plus, 
  Smile, Image as ImageIcon, CheckCheck, Circle, Eye, History, Calendar, Phone, 
  Video, Shield, Lock, Unlock, ChevronDown, ChevronRight, BarChart3, Zap, Star, 
  Flag, Archive, Inbox, TrendingUp, Target, Award, Headphones, Settings, Copy, 
  ExternalLink, Bell, Bookmark, Pin, Hash, FileText, UserCheck, AlertTriangle, 
  Volume2, ArrowUp, ArrowDown, MoreHorizontal, Filter, Mic, Paperclip, Bot,
  Maximize2, Minimize2, PhoneCall, VideoIcon, Info, ChevronLeft, Layers,
  Power, PowerOff, DoorOpen, DoorClosed, Pencil, Trash2, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField, JewelChipRow } from '../../components/ui/jewel-modal';
import CustomerProfilePanel from '../../components/livechat/CustomerProfilePanel';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/uploads/')) return `${BACKEND_URL}${path}`;
  if (path.startsWith('/uploads/')) return `${BACKEND_URL}/api${path}`;
  return `${BACKEND_URL}${path}`;
};

const EMOJI_LIST = ['😀', '😂', '😍', '🥰', '😎', '🤔', '👍', '👎', '❤️', '🔥', '✅', '⭐', '🎉', '💯', '🙏', '👋', '😊', '🤝', '💪', '🚀'];

const QUICK_RESPONSES = [
  { id: 'greeting', label: 'Greeting', text: "Hi there! 👋 Thanks for reaching out. How can I help you today?", category: 'general', icon: '👋' },
  { id: 'hold', label: 'Please Hold', text: "Please hold on for a moment while I look into this for you. I'll be right back!", category: 'general', icon: '⏳' },
  { id: 'transfer', label: 'Transfer', text: "I'm going to transfer you to a specialist who can better assist. Please hold...", category: 'general', icon: '🔄' },
  { id: 'thanks', label: 'Thank You', text: "Thank you for your patience! Is there anything else I can help you with?", category: 'closing', icon: '🙏' },
  { id: 'resolved', label: 'Resolved', text: "I'm glad I could help! If you have any other questions, feel free to reach out. Have a great day! 🌟", category: 'closing', icon: '✅' },
  { id: 'hours', label: 'Hours', text: "Our support hours are Monday-Friday, 9am-6pm. We'll respond as soon as we're back!", category: 'info', icon: '🕐' },
  { id: 'refund', label: 'Refund', text: "I understand you'd like info about refunds. Our policy allows refunds within 30 days. Let me check your case.", category: 'sales', icon: '💰' },
  { id: 'technical', label: 'Technical', text: "I'd be happy to help with this technical issue. Could you provide more details?", category: 'support', icon: '🔧' },
  { id: 'escalate', label: 'Escalate', text: "I understand this is complex. Let me escalate this to our senior team for you.", category: 'support', icon: '⬆️' },
  { id: 'feedback', label: 'Feedback', text: "We really appreciate your feedback! Would you mind sharing your thoughts?", category: 'closing', icon: '💬' },
];

const PRIORITY_OPTIONS = [
  { id: 'urgent', label: 'Urgent', color: 'red', icon: AlertTriangle },
  { id: 'high', label: 'High', color: 'orange', icon: ArrowUp },
  { id: 'normal', label: 'Normal', color: 'blue', icon: Circle },
  { id: 'low', label: 'Low', color: 'slate', icon: ArrowDown },
  { id: 'vip', label: 'VIP', color: 'purple', icon: Star },
];

const LiveChatPageRevamped = ({ getAuthHeaders, currentUser }) => {
  // Core State
  const [chatSessions, setChatSessions] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatResponse, setChatResponse] = useState('');
  const [selectedChatInfo, setSelectedChatInfo] = useState(null);
  
  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showQuickResponses, setShowQuickResponses] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAITools, setShowAITools] = useState(false);
  const [sidebarView, setSidebarView] = useState('chats');
  const [rightPanelView, setRightPanelView] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Notes State
  const [privateNotes, setPrivateNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  
  // Image State
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Settings State
  const [chatPriority, setChatPriority] = useState({});
  const [bookmarkedChats, setBookmarkedChats] = useState([]);
  const [restrictedChats, setRestrictedChats] = useState({});
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // History State
  const [resolvedSessions, setResolvedSessions] = useState([]);
  const [historySelectedChat, setHistorySelectedChat] = useState(null);
  const [historyMessages, setHistoryMessages] = useState([]);
  
  // Stats State
  const [todayStats, setTodayStats] = useState({ resolved: 0, avgTime: '2m', satisfaction: 95 });
  
  // Loading State
  const [aiLoading, setAiLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Transfer State
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('');
  
  // Live Chat Status State (Open/Closed)
  const [chatSystemStatus, setChatSystemStatus] = useState({ is_open: true, message: null, updated_by: null });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [closedMessage, setClosedMessage] = useState("We're currently closed. Please try again during our operating hours.");

  // Edit / Delete State
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Typing Indicators State
  const [customerTyping, setCustomerTyping] = useState(false);
  const [customerTypingName, setCustomerTypingName] = useState('');
  const [staffIsTyping, setStaffIsTyping] = useState(false);
  const [failedAvatars, setFailedAvatars] = useState({});
  
  // Canned Responses
  const [cannedResponses, setCannedResponses] = useState([]);
  const [showCannedModal, setShowCannedModal] = useState(false);
  const [newCanned, setNewCanned] = useState({ title: '', content: '', category: 'general' });
  
  // Analytics
  const [chatAnalytics, setChatAnalytics] = useState(null);
  
  // Shift Manager & Agent Status
  const [agentStatuses, setAgentStatuses] = useState([]);
  const [myAgentStatus, setMyAgentStatus] = useState('available');
  const [shiftManagers, setShiftManagers] = useState([]);
  const [showShiftManagerModal, setShowShiftManagerModal] = useState(false);
  const [selectedNewManager, setSelectedNewManager] = useState('');
  const [customerNotes, setCustomerNotes] = useState([]);
  const [newCustomerNote, setNewCustomerNote] = useState('');
  const [customerHistory, setCustomerHistory] = useState([]);
  const [selectedBulkChats, setSelectedBulkChats] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [chatTags, setChatTags] = useState({});
  const [soundNotify, setSoundNotify] = useState(true);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);

  // ─────────  UNIFIED SETTINGS  ─────────
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState('general');
  const [chatSettings, setChatSettings] = useState({
    desktopNotifications: false,
    autoRefreshSec: 30,
    slaWarnMin: 3,
    autoAwayMin: 10,
    welcomeMessage: 'Hi 👋 Thanks for reaching out — an agent will be with you shortly.',
    autoCloseAfterHours: false,
    openHour: 9,
    closeHour: 22,
    compactList: false,
  });
  // Load / persist settings
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`livechat_settings_${currentUser?.username || 'anon'}`);
      if (raw) setChatSettings(prev => ({ ...prev, ...JSON.parse(raw) }));
      const s = localStorage.getItem(`livechat_sound_${currentUser?.username || 'anon'}`);
      if (s !== null) setSoundEnabled(s === '1');
    } catch {}
  }, [currentUser?.username]);
  const saveSettings = (partial) => {
    const next = { ...chatSettings, ...partial };
    setChatSettings(next);
    localStorage.setItem(`livechat_settings_${currentUser?.username || 'anon'}`, JSON.stringify(next));
  };
  const toggleDesktopNotifs = async () => {
    if (!('Notification' in window)) { toast.error('This browser does not support notifications'); return; }
    if (chatSettings.desktopNotifications) {
      saveSettings({ desktopNotifications: false });
      toast.success('Desktop notifications disabled');
      return;
    }
    const p = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (p === 'granted') {
      saveSettings({ desktopNotifications: true });
      toast.success('Desktop notifications enabled');
      try { new Notification('Live Chat', { body: 'Notifications are on for new customer chats.' }); } catch {}
    } else {
      toast.error('Notification permission denied');
    }
  };
  const exportChatData = () => {
    const payload = {
      exported_at: new Date().toISOString(),
      exported_by: currentUser?.username,
      settings: chatSettings,
      canned_replies: cannedResponses,
      shift_managers: shiftManagers.map(m => ({ username: m.username, role: m.role, assigned_by: m.assigned_by })),
      system_status: chatSystemStatus,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `live-chat-config-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Config exported');
  };
  
  const isExec = ['superadmin', 'executive'].includes(currentUser?.role);
  const isShiftManager = currentUser?.is_shift_manager || isExec;
  
  // Refs
  const chatMessagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const isExecutive = ['superadmin', 'executive', 'admin'].includes(currentUser?.role);
  const isManagement = ['superadmin', 'executive', 'admin', 'manager'].includes(currentUser?.role);

  // Initialize
  useEffect(() => {
    fetchChatSessions();
    fetchStaffMembers();
    fetchTodayStats();
    fetchChatSystemStatus();
    fetchCannedResponses();
    fetchChatAnalytics();
    fetchAgentStatuses();
    fetchShiftManagers();
    // Set own status to available on mount
    updateMyAgentStatus('available');
    
    const savedBookmarks = localStorage.getItem('livechat_bookmarks');
    if (savedBookmarks) setBookmarkedChats(JSON.parse(savedBookmarks));
    const savedPriorities = localStorage.getItem('livechat_priorities');
    if (savedPriorities) setChatPriority(JSON.parse(savedPriorities));
    const savedRestrictions = localStorage.getItem('livechat_restrictions');
    if (savedRestrictions) setRestrictedChats(JSON.parse(savedRestrictions));
    
    const interval = setInterval(fetchChatSessions, 12000);
    return () => clearInterval(interval);
  }, []);

  // Fetch messages + customer typing when chat selected
  useEffect(() => {
    if (selectedChat) {
      fetchChatHistory(selectedChat);
      fetchChatInfo(selectedChat);
      fetchPrivateNotes(selectedChat);
      const interval = setInterval(() => {
        fetchChatHistory(selectedChat);
        fetchCustomerTypingStatus(selectedChat);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [selectedChat]);

  // Auto-scroll messages
  useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, customerTyping]);

  // Staff typing indicator: send typing event on input change
  const handleTypingInput = useCallback((e) => {
    setChatResponse(e.target.value);

    if (!selectedChat) return;
    const headers = getAuthHeaders();
    if (!headers) return;

    // Send typing start
    if (!staffIsTyping) {
      setStaffIsTyping(true);
      axios.post(`${BACKEND_URL}/api/chat/typing/${selectedChat}`, {}, { headers }).catch(() => {});
    }

    // Reset timeout to clear typing after 3s of no input
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setStaffIsTyping(false);
      axios.delete(`${BACKEND_URL}/api/chat/typing/${selectedChat}`, { headers }).catch(() => {});
    }, 3000);
  }, [selectedChat, staffIsTyping, getAuthHeaders]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  // API Functions
  const fetchChatSessions = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/chat/sessions`, { headers });
      setChatSessions(response.data);
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
    }
  };

  const fetchChatHistory = async (sessionId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/history/${sessionId}`);
      setChatMessages(response.data);
      // Also fetch customer notes and history
      fetchCustomerNotes(sessionId);
      fetchCustomerHistory(sessionId);
    } catch (error) {}
  };

  const fetchChatInfo = async (sessionId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/session-info/${sessionId}`);
      setSelectedChatInfo(response.data);
    } catch (error) {
      setSelectedChatInfo(null);
    }
  };

  const fetchPrivateNotes = async (sessionId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/chat/notes/${sessionId}`, { headers });
      setPrivateNotes(response.data || []);
    } catch (error) {
      setPrivateNotes([]);
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/admin/staff-accounts`, { headers });
      setStaffMembers(response.data.filter(s => s.username !== currentUser?.username));
    } catch (error) {}
  };

  const fetchTodayStats = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/chat/stats/today`, { headers });
      setTodayStats(response.data);
    } catch (error) {
      setTodayStats({ resolved: 0, avgTime: '2m', satisfaction: 95 });
    }
  };

  const fetchResolvedHistory = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/chat/history/resolved`, { headers });
      setResolvedSessions(response.data);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const fetchHistoryMessages = async (sessionId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/history/${sessionId}`);
      setHistoryMessages(response.data);
    } catch (error) {}
  };

  const fetchChatSystemStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/system-status`);
      setChatSystemStatus(response.data);
    } catch (error) {
      console.error('Error fetching chat system status:', error);
    }
  };

  const fetchCannedResponses = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/calling/canned-responses`, { headers });
      setCannedResponses(res.data || []);
    } catch {}
  };

  const fetchChatAnalytics = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/calling/chat-analytics`, { headers });
      setChatAnalytics(res.data);
    } catch {}
  };

  const handleSaveCannedResponse = async () => {
    if (!newCanned.title.trim() || !newCanned.content.trim()) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/calling/canned-responses`, newCanned, { headers });
      setNewCanned({ title: '', content: '', category: 'general' });
      setShowCannedModal(false);
      fetchCannedResponses();
      toast.success('Response saved');
    } catch { toast.error('Failed to save'); }
  };

  const handleDeleteCannedResponse = async (id) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.delete(`${BACKEND_URL}/api/calling/canned-responses/${id}`, { headers });
      fetchCannedResponses();
      toast.success('Deleted');
    } catch {}
  };

  const fetchAgentStatuses = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/agent-statuses`, { headers });
      setAgentStatuses(res.data || []);
    } catch {}
  };

  const fetchShiftManagers = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/shift-managers`, { headers });
      setShiftManagers(res.data || []);
    } catch {}
  };

  const updateMyAgentStatus = async (status) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.put(`${BACKEND_URL}/api/admin/agent-status?status=${status}`, {}, { headers });
      setMyAgentStatus(status);
      fetchAgentStatuses();
    } catch {}
  };

  const handleAssignShiftManager = async () => {
    if (!selectedNewManager) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/admin/shift-managers/${selectedNewManager}`, {}, { headers });
      toast.success('Shift manager assigned');
      setSelectedNewManager('');
      fetchShiftManagers();
    } catch { toast.error('Failed'); }
  };

  const handleRemoveShiftManager = async (username) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.delete(`${BACKEND_URL}/api/admin/shift-managers/${username}`, { headers });
      toast.success('Shift manager removed');
      fetchShiftManagers();
    } catch { toast.error('Failed'); }
  };

  const fetchCustomerNotes = async (sessionId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/customer-notes/${sessionId}`, { headers });
      setCustomerNotes(res.data || []);
    } catch {}
  };

  const addCustomerNote = async () => {
    if (!newCustomerNote.trim() || !selectedChat) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/admin/customer-notes/${selectedChat}?note=${encodeURIComponent(newCustomerNote)}`, {}, { headers });
      setNewCustomerNote('');
      fetchCustomerNotes(selectedChat);
      toast.success('Note added');
    } catch {}
  };

  const fetchCustomerHistory = async (sessionId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/customer-history/${sessionId}`, { headers });
      setCustomerHistory(res.data || []);
    } catch {}
  };

  const handleBulkAction = async (action, targetUsername) => {
    if (selectedBulkChats.length === 0) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/admin/bulk-chat-action`, {
        session_ids: selectedBulkChats,
        action,
        target_username: targetUsername || null
      }, { headers });
      toast.success(`${action} completed for ${selectedBulkChats.length} chats`);
      setSelectedBulkChats([]);
      setShowBulkActions(false);
      fetchChatSessions();
    } catch { toast.error('Failed'); }
  };

  const toggleBulkSelect = (sessionId) => {
    setSelectedBulkChats(prev => 
      prev.includes(sessionId) ? prev.filter(id => id !== sessionId) : [...prev, sessionId]
    );
  };

  const addChatTag = async (sessionId, tag) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.put(`${BACKEND_URL}/api/admin/chat-tag/${sessionId}?tag=${encodeURIComponent(tag)}`, {}, { headers });
      setChatTags(prev => ({ ...prev, [sessionId]: [...(prev[sessionId] || []), tag] }));
    } catch {}
  };



  const fetchCustomerTypingStatus = async (sessionId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/chat/customer-typing-status/${sessionId}`, { headers });
      setCustomerTyping(response.data.customer_typing);
      setCustomerTypingName(response.data.customer_name || 'Customer');
    } catch {
      setCustomerTyping(false);
    }
  };

  const handleToggleChatStatus = async (newStatus) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/chat/system-status`, {
        is_open: newStatus, message: newStatus ? null : closedMessage
      }, { headers });
      setChatSystemStatus(prev => ({ ...prev, is_open: newStatus, message: newStatus ? null : closedMessage, updated_by: currentUser?.username }));
      toast.success(newStatus ? 'Live chat is now OPEN' : 'Live chat is now CLOSED');
      setShowStatusModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update chat status');
    }
  };

  // Action Functions
  const handleClaimChat = async (sessionId) => {
    if (restrictedChats[sessionId] && !isManagement) {
      toast.error('This chat is restricted to managers+');
      return;
    }
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/chat/claim/${sessionId}`, {}, { headers });
      toast.success('Chat claimed!');
      setSelectedChat(sessionId);
      fetchChatSessions();
    } catch (error) {
      toast.error('Failed to claim chat');
    }
  };

  const handleSendResponse = async () => {
    if ((!chatResponse.trim() && !selectedImage) || !selectedChat) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      let imageUrl = null;
      if (selectedImage) {
        const formData = new FormData();
        formData.append('image', selectedImage);
        const uploadRes = await axios.post(`${BACKEND_URL}/api/chat/upload-image`, formData, {
          headers: { Authorization: headers.Authorization }
        });
        imageUrl = uploadRes.data.image_url;
      }

      await axios.post(`${BACKEND_URL}/api/chat/respond`, {
        session_id: selectedChat, message: chatResponse || '📷 Image', image_url: imageUrl
      }, { headers });
      
      setChatResponse('');
      setSelectedImage(null);
      setImagePreview(null);
      setShowEmojiPicker(false);
      setShowQuickResponses(false);
      // Clear typing indicator after sending
      setStaffIsTyping(false);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      axios.delete(`${BACKEND_URL}/api/chat/typing/${selectedChat}`, { headers }).catch(() => {});
      fetchChatHistory(selectedChat);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleResolveChat = async (sessionId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/chat/resolve/${sessionId}`, {}, { headers });
      toast.success('Chat resolved!');
      setSelectedChat(null);
      setChatMessages([]);
      setSelectedChatInfo(null);
      fetchChatSessions();
      fetchTodayStats();
    } catch (error) {
      toast.error('Failed to resolve chat');
    }
  };

  const handleTransferChat = async () => {
    if (!selectedStaff || !selectedChat) { toast.error('Please select a staff member'); return; }
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/calling/chat-transfer`, {
        session_id: selectedChat, target_username: selectedStaff
      }, { headers });
      toast.success(`Chat transferred to ${selectedStaff}`);
      setShowTransferModal(false);
      setSelectedChat(null);
      fetchChatSessions();
    } catch { toast.error('Failed to transfer'); }
  };

  const handleAISummary = async () => {
    if (!selectedChat) return;
    setSummaryLoading(true);
    setShowSummaryModal(true);
    setSummaryText('');
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const r = await axios.post(`${BACKEND_URL}/api/chat/ai-summary/${selectedChat}`, {}, { headers });
      setSummaryText(r.data.summary || 'No summary available.');
    } catch (err) {
      setSummaryText('');
      toast.error(err.response?.data?.detail || 'AI summary failed');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedChat) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/chat/notes/${selectedChat}`, {
        note: newNote, author: currentUser?.username
      }, { headers });
      setNewNote('');
      fetchPrivateNotes(selectedChat);
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.delete(`${BACKEND_URL}/api/chat/notes/${selectedChat}/${noteId}`, { headers });
      fetchPrivateNotes(selectedChat);
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleAIImprove = async (action) => {
    if (!chatResponse.trim()) { toast.error('Please type a message first'); return; }
    setAiLoading(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.post(`${BACKEND_URL}/api/chat/ai-improve`, { text: chatResponse, action }, { headers });
      setChatResponse(response.data.improved_text);
      setShowAITools(false);
      toast.success('Message enhanced!');
    } catch (error) {
      toast.error('Failed to improve message');
    } finally {
      setAiLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSetPriority = (sessionId, priority) => {
    const newPriorities = { ...chatPriority, [sessionId]: priority };
    setChatPriority(newPriorities);
    localStorage.setItem('livechat_priorities', JSON.stringify(newPriorities));
    toast.success(`Priority: ${priority.label}`);
  };

  const handleBookmarkChat = (sessionId) => {
    let newBookmarks;
    if (bookmarkedChats.includes(sessionId)) {
      newBookmarks = bookmarkedChats.filter(id => id !== sessionId);
    } else {
      newBookmarks = [...bookmarkedChats, sessionId];
    }
    setBookmarkedChats(newBookmarks);
    localStorage.setItem('livechat_bookmarks', JSON.stringify(newBookmarks));
  };

  const handleRestrictChat = (sessionId) => {
    const newRestrictions = { ...restrictedChats };
    if (newRestrictions[sessionId]) { delete newRestrictions[sessionId]; }
    else { newRestrictions[sessionId] = true; }
    setRestrictedChats(newRestrictions);
    localStorage.setItem('livechat_restrictions', JSON.stringify(newRestrictions));
  };

  const handleManagerTakeover = async (sessionId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/chat/claim/${sessionId}`, { takeover: true }, { headers });
      toast.success('Chat taken over');
      setSelectedChat(sessionId);
      fetchChatSessions();
    } catch (error) {
      toast.error('Failed to takeover chat');
    }
  };

  // ==========================================
  // Edit / Delete Message Handlers
  // ==========================================
  const handleStartEdit = (msg) => {
    setEditingMessageId(msg.message_id);
    setEditText(msg.message);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  const handleSaveEdit = async (messageId) => {
    if (!editText.trim()) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.put(`${BACKEND_URL}/api/chat/messages/${messageId}`, { message: editText }, { headers });
      toast.success('Message edited');
      setEditingMessageId(null);
      setEditText('');
      fetchChatHistory(selectedChat);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to edit message');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.delete(`${BACKEND_URL}/api/chat/messages/${messageId}`, { headers });
      toast.success('Message deleted');
      setShowDeleteConfirm(null);
      fetchChatHistory(selectedChat);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  // Filter & Sort
  const filteredSessions = chatSessions.filter(session => {
    const matchesSearch = session.session_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.user_email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || session.status === filterStatus;
    return matchesSearch && matchesFilter;
  }).sort((a, b) => {
    const priorityOrder = { urgent: 0, high: 1, vip: 2, normal: 3, low: 4 };
    const aPriority = chatPriority[a.session_id]?.id || 'normal';
    const bPriority = chatPriority[b.session_id]?.id || 'normal';
    if (priorityOrder[aPriority] !== priorityOrder[bPriority]) {
      return priorityOrder[aPriority] - priorityOrder[bPriority];
    }
    return new Date(b.last_message_time) - new Date(a.last_message_time);
  });

  const waitingCount = chatSessions.filter(s => s.status === 'active').length;
  const activeCount = chatSessions.filter(s => s.status === 'claimed').length;
  const myChatsCount = chatSessions.filter(s => s.claimed_by === currentUser?.username).length;

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Check if current user can edit a message (own messages only)
  const canModifyMessage = (msg) => {
    if (!msg.message_id) return false;
    if (msg.sender_type !== 'staff') return false;
    if (msg.deleted) return false;
    return msg.sender_username === currentUser?.username;
  };

  // Check if current user can delete a message (own + executives)
  const canDeleteMessage = (msg) => {
    if (!msg.message_id) return false;
    if (msg.sender_type !== 'staff') return false;
    if (msg.deleted) return false;
    if (msg.sender_username === currentUser?.username) return true;
    if (isExecutive) return true;
    return false;
  };

  return (
    <div className={`relative ${isFullscreen ? 'fixed inset-0 z-50' : 'h-[calc(100vh-100px)]'} flex flex-col`} data-testid="live-chat-page"
      style={{ background: isFullscreen ? '#05050A' : undefined, fontFamily: "'Inter', sans-serif" }}>
      {/* Ambient luxury glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-0 left-1/4 w-[600px] h-[300px] bg-[#00E5FF]/[0.04] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[280px] bg-[#8A2BE2]/[0.04] rounded-full blur-[140px]" />
      </div>

      {/* Header */}
      <div className="relative flex items-center justify-between mb-5 px-1 flex-wrap gap-2">
        <div className="flex items-center gap-4">
          <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center overflow-hidden border border-white/10"
            style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.18), rgba(138,43,226,0.18))', boxShadow: '0 0 32px rgba(0,229,255,0.12)' }}>
            <Headphones className="w-5 h-5 text-white relative z-10" strokeWidth={1.5} />
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
          </div>
          <div>
            <div className="inline-flex items-center gap-1.5 mb-0.5">
              <span className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-medium">Concierge</span>
              <span className="w-1 h-1 rounded-full bg-[#00E5FF]/60" />
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#00E5FF]/70 font-medium">Live</span>
            </div>
            <h1 className="text-2xl font-light text-white tracking-tighter">
              Live <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">Chat</span>
            </h1>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 border backdrop-blur-sm ${
            chatSystemStatus.is_open ? 'bg-emerald-500/[0.08] border-emerald-500/25' : 'bg-red-500/[0.08] border-red-500/25'
          }`}>
            {chatSystemStatus.is_open ? (
              <><DoorOpen className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-300 text-[11px] font-medium tracking-[0.1em] uppercase">Open</span></>
            ) : (
              <><DoorClosed className="w-3.5 h-3.5 text-red-400" /><span className="text-red-300 text-[11px] font-medium tracking-[0.1em] uppercase">Closed</span></>
            )}
          </div>

          {waitingCount > 0 && (
            <div className="flex items-center gap-1.5 bg-violet-500/[0.08] border border-violet-500/25 rounded-full px-3.5 py-1.5 backdrop-blur-sm">
              <span className="relative flex">
                <span className="absolute inline-flex h-2 w-2 rounded-full bg-violet-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-400" />
              </span>
              <span className="text-violet-300 font-medium text-sm tabular-nums">{waitingCount}</span>
              <span className="text-violet-300/60 text-[10px] tracking-[0.1em] uppercase">waiting</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-full px-3.5 py-1.5 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-emerald-300 font-medium text-sm tabular-nums">{activeCount}</span>
            <span className="text-emerald-300/60 text-[10px] tracking-[0.1em] uppercase">active</span>
          </div>

          <div className="flex items-center bg-white/[0.03] backdrop-blur-md border border-white/[0.06] rounded-full p-0.5 ml-1">
            <Button onClick={() => { const next = !soundEnabled; setSoundEnabled(next); localStorage.setItem(`livechat_sound_${currentUser?.username || 'anon'}`, next ? '1' : '0'); }} variant="ghost" size="sm"
              className={`h-8 w-8 p-0 rounded-full ${soundEnabled ? 'text-[#00E5FF] bg-[#00E5FF]/10' : 'text-white/40 hover:text-white/70'}`}
              title={soundEnabled ? 'Mute' : 'Unmute'}>
              <Volume2 className="w-3.5 h-3.5" />
            </Button>
            <Button onClick={() => setIsFullscreen(!isFullscreen)} variant="ghost" size="sm"
              className="h-8 w-8 p-0 rounded-full text-white/40 hover:text-white/80" title="Fullscreen">
              {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </Button>
            <Button onClick={() => { fetchChatSessions(); toast.success('Refreshed'); }} variant="ghost" size="sm"
              className="h-8 w-8 p-0 rounded-full text-white/40 hover:text-white/80" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            {isExecutive && (
              <Button onClick={() => setShowSettingsModal(true)} variant="ghost" size="sm" data-testid="livechat-settings-btn"
                className="h-8 w-8 p-0 rounded-full text-white/40 hover:text-[#00E5FF] hover:bg-[#00E5FF]/10" title="Settings">
                <Settings className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          {isExecutive && (
            <Button onClick={() => { setSidebarView('history'); fetchResolvedHistory(); }}
              className="h-9 px-3.5 rounded-full text-[11px] tracking-[0.05em] font-medium bg-[#8A2BE2]/10 text-[#a673e8] border border-[#8A2BE2]/25 hover:bg-[#8A2BE2]/20">
              <History className="w-3.5 h-3.5 mr-1.5" /> History
            </Button>
          )}
        </div>

        {/* Agent Status Bar */}
        <div className="w-full flex items-center gap-3 px-4 py-2 rounded-2xl bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] mt-2" data-testid="agent-status-bar">
          <span className="text-[10px] text-white/30 uppercase tracking-[0.15em] font-medium">My Status</span>
          <div className="flex gap-1">
            {['available', 'busy', 'away'].map(s => (
              <button key={s} onClick={() => updateMyAgentStatus(s)} data-testid={`agent-status-${s}`}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-medium tracking-[0.05em] transition-all ${
                  myAgentStatus === s
                    ? s === 'available' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : s === 'busy' ? 'bg-red-500/15 text-red-300 border border-red-500/30' : 'bg-violet-500/15 text-violet-300 border border-violet-500/30'
                    : 'text-white/40 hover:text-white/70 border border-transparent hover:bg-white/[0.03]'
                }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${s === 'available' ? 'bg-emerald-400' : s === 'busy' ? 'bg-red-400' : 'bg-violet-400'}`} />
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-[10px] text-white/40">
              <Users className="w-3 h-3" /> {agentStatuses.filter(a => a.status === 'available').length} online
            </div>
            {selectedBulkChats.length > 0 && (
              <div className="flex items-center gap-2">
                <Badge className="bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/25 text-[10px]">{selectedBulkChats.length} selected</Badge>
                <Button size="sm" onClick={() => handleBulkAction('resolve')} className="h-6 px-2 text-[10px] rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="bulk-resolve-btn">
                  <CheckCircle className="w-3 h-3 mr-1" /> Resolve all
                </Button>
                <Button size="sm" onClick={() => setSelectedBulkChats([])} variant="ghost" className="h-6 px-2 text-[10px] text-white/40">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
            {isExec && (
              <Button size="sm" onClick={() => { setSettingsTab('managers'); setShowSettingsModal(true); }} variant="ghost" className="h-7 text-[10px] text-[#a673e8] hover:bg-[#8A2BE2]/10 rounded-full" data-testid="manage-shift-managers-btn">
                <Shield className="w-3 h-3 mr-1" /> Shift Managers
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Sidebar */}
        <div className="w-80 flex flex-col bg-[#0A0A12]/60 backdrop-blur-md border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="flex border-b border-white/[0.06]">
            {[
              { id: 'chats', label: 'Chats', icon: MessageCircle, count: chatSessions.length },
              { id: 'agents', label: 'Agents', icon: Users, count: agentStatuses.filter(a => a.status === 'available').length },
              { id: 'stats', label: 'Stats', icon: BarChart3 },
            ].map(tab => (
              <button key={tab.id} onClick={() => setSidebarView(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-medium uppercase tracking-[0.1em] transition-all border-b-2 ${
                  sidebarView === tab.id ? 'text-[#00E5FF] border-[#00E5FF]' : 'text-white/40 hover:text-white/70 border-transparent'
                }`}>
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
                {tab.count !== undefined && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/[0.06] text-white/60">{tab.count}</span>}
              </button>
            ))}
          </div>

          {sidebarView === 'chats' && (
            <>
              <div className="p-3 border-b border-white/[0.06] space-y-2.5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                  <input type="text" placeholder="Search conversations…" value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-full pl-9 pr-4 py-2 text-white text-xs placeholder-white/30 focus:outline-none focus:border-[#00E5FF]/30 transition-colors" />
                </div>
                <div className="flex gap-1">
                  {['all', 'active', 'claimed'].map(status => (
                    <button key={status} onClick={() => setFilterStatus(status)}
                      className={`flex-1 py-1.5 rounded-full text-[10px] font-medium tracking-[0.05em] uppercase transition-all ${
                        filterStatus === status
                          ? 'bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/25'
                          : 'bg-white/[0.02] text-white/40 border border-transparent hover:border-white/[0.06] hover:text-white/70'
                      }`}>
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {filteredSessions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-12 h-12 rounded-2xl bg-white/[0.02] border border-white/[0.05] flex items-center justify-center mb-3">
                      <Inbox className="w-5 h-5 text-white/20" />
                    </div>
                    <p className="text-white/30 text-xs tracking-[0.1em] uppercase">No conversations</p>
                  </div>
                ) : (
                  filteredSessions.map(session => {
                    const priority = chatPriority[session.session_id];
                    const isBookmarked = bookmarkedChats.includes(session.session_id);
                    const isRestricted = restrictedChats[session.session_id];
                    const isSelected = selectedChat === session.session_id;
                    return (
                      <div key={session.session_id} onClick={() => setSelectedChat(session.session_id)}
                        data-testid={`chat-session-${session.session_id}`}
                        className={`relative p-3 rounded-xl cursor-pointer transition-all group overflow-hidden ${
                          isSelected
                            ? 'bg-gradient-to-r from-[#00E5FF]/[0.08] to-[#8A2BE2]/[0.05] border border-[#00E5FF]/30'
                            : 'bg-white/[0.015] border border-transparent hover:border-white/[0.08] hover:bg-white/[0.03]'
                        }`}>
                        {isSelected && <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00E5FF] to-[#8A2BE2]" />}
                        {priority && <div className={`absolute right-2 top-2 w-1.5 h-1.5 rounded-full bg-${priority.color}-400 shadow-lg shadow-${priority.color}-500/50`} />}
                        <div className="flex items-start gap-3">
                          <div className="relative flex-shrink-0">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
                              session.status === 'active'
                                ? 'bg-violet-500/[0.08] text-violet-300 border-violet-500/20'
                                : 'bg-emerald-500/[0.08] text-emerald-300 border-emerald-500/20'
                            }`}>
                              <User className="w-4 h-4" strokeWidth={1.5} />
                            </div>
                            {session.user_online && (
                              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-[#0A0A12]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-white font-medium text-sm truncate tracking-tight">
                                {session.user_name || `Guest ${session.session_id.slice(0, 6)}`}
                              </p>
                              {isBookmarked && <Bookmark className="w-3 h-3 text-[#00E5FF] fill-[#00E5FF]" />}
                              {isRestricted && <Lock className="w-3 h-3 text-orange-400" />}
                            </div>
                            {session.last_message && <p className="text-white/40 text-xs truncate mt-0.5 leading-relaxed">{session.last_message}</p>}
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-white/25 text-[10px] tracking-wider">{formatTime(session.last_message_time)}</span>
                              <span className={`text-[9px] tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-full ${
                                session.status === 'active'
                                  ? 'bg-violet-500/10 text-violet-300 border border-violet-500/20'
                                  : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                              }`}>
                                {session.status === 'active' ? 'Waiting' : 'Active'}
                              </span>
                            </div>
                          </div>
                        </div>
                        {session.status === 'active' && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" onClick={(e) => { e.stopPropagation(); handleClaimChat(session.session_id); }}
                              data-testid={`claim-chat-${session.session_id}`}
                              className="h-7 px-3 rounded-full text-[10px] uppercase tracking-[0.1em] font-medium bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black">
                              Claim
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}


          {/* Agents Tab */}
          {sidebarView === 'agents' && (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-cyan-400" /> Team
              </h3>
              {agentStatuses.map(agent => {
                const statusColors = { available: 'bg-emerald-500', busy: 'bg-red-500', away: 'bg-violet-500', offline: 'bg-slate-600' };
                const statusLabels = { available: 'Available', busy: 'Busy', away: 'Away', offline: 'Offline' };
                return (
                  <div key={agent.username} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-800/30 border border-slate-800 hover:border-slate-700 transition-all"
                    data-testid={`agent-card-${agent.username}`}>
                    <div className="relative">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/20">
                        <span className="text-xs font-bold text-cyan-400">{(agent.display_name || agent.username)?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-slate-900 ${statusColors[agent.status] || statusColors.offline}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-medium truncate">{agent.display_name || agent.username}</p>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] ${agent.status === 'available' ? 'text-emerald-400' : agent.status === 'busy' ? 'text-red-400' : agent.status === 'away' ? 'text-violet-400' : 'text-slate-500'}`}>
                          {statusLabels[agent.status] || 'Offline'}
                        </span>
                        {agent.is_shift_manager && <Badge className="bg-purple-500/20 text-purple-400 text-[8px] px-1 py-0">SM</Badge>}
                      </div>
                    </div>
                    <span className="text-[9px] text-slate-600 capitalize">{agent.role}</span>
                  </div>
                );
              })}
            </div>
          )}

          {sidebarView === 'stats' && (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-400" /> Performance
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mb-2" />
                  <p className="text-2xl font-bold text-white">{chatAnalytics?.resolved_today || todayStats.resolved}</p>
                  <p className="text-emerald-400/70 text-xs">Resolved Today</p>
                </div>
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
                  <Layers className="w-5 h-5 text-cyan-400 mb-2" />
                  <p className="text-2xl font-bold text-white">{chatAnalytics?.resolved_week || 0}</p>
                  <p className="text-cyan-400/70 text-xs">This Week</p>
                </div>
                <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
                  <Star className="w-5 h-5 text-violet-400 mb-2" />
                  <p className="text-2xl font-bold text-white">{chatAnalytics?.avg_rating || '—'}<span className="text-xs text-slate-500">/5</span></p>
                  <p className="text-violet-400/70 text-xs">Avg Rating</p>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                  <Award className="w-5 h-5 text-purple-400 mb-2" />
                  <p className="text-2xl font-bold text-white">{chatAnalytics?.my_resolved || myChatsCount}</p>
                  <p className="text-purple-400/70 text-xs">My Resolved</p>
                </div>
              </div>

              {/* Canned Responses */}
              <div className="border-t border-slate-800 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                    <Bookmark className="w-4 h-4 text-violet-400" /> Saved Responses
                  </h3>
                  <Button size="sm" variant="ghost" onClick={() => setShowCannedModal(true)}
                    className="text-cyan-400 hover:text-cyan-300 h-7 px-2 text-xs" data-testid="add-canned-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> New
                  </Button>
                </div>
                {cannedResponses.length === 0 ? (
                  <p className="text-slate-600 text-xs text-center py-3">No saved responses yet</p>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {cannedResponses.map(cr => (
                      <div key={cr.id} className="group flex items-center gap-2 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer transition-all"
                        onClick={() => { setChatResponse(cr.content); toast.success('Response loaded'); }}>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-medium truncate">{cr.title}</p>
                          <p className="text-slate-500 text-[10px] truncate">{cr.content.substring(0, 60)}...</p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteCannedResponse(cr.id); }}
                          className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all p-1">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <h4 className="text-white font-medium text-sm mb-3">Quick Tips</h4>
                <div className="space-y-2 text-xs text-slate-400">
                  <p>Use saved responses for consistent replies</p>
                  <p>AI tools can enhance your messages</p>
                  <p>Transfer chats to specialists when needed</p>
                  <p>Customer typing shown in real-time</p>
                </div>
              </div>
            </div>
          )}

          {sidebarView === 'history' && (
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              <div className="flex items-center justify-between p-2">
                <h3 className="text-white font-semibold text-sm">Chat History</h3>
                <Button size="sm" variant="ghost" onClick={() => setSidebarView('chats')}
                  className="text-slate-400 hover:text-white h-7 w-7 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {resolvedSessions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Archive className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No resolved chats</p>
                </div>
              ) : (
                resolvedSessions.map(session => (
                  <div key={session.session_id}
                    onClick={() => { setHistorySelectedChat(session); fetchHistoryMessages(session.session_id); }}
                    className={`p-3 rounded-xl cursor-pointer transition-all ${
                      historySelectedChat?.session_id === session.session_id
                        ? 'bg-purple-500/20 border border-purple-500/40' : 'bg-slate-800/30 hover:bg-slate-800/50'
                    }`}>
                    <p className="text-white text-sm font-medium truncate">{session.user_name || 'Guest'}</p>
                    <p className="text-slate-500 text-xs mt-0.5">{formatTime(session.resolved_at)}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col bg-[#0A0A12]/60 backdrop-blur-md border border-white/[0.06] rounded-2xl overflow-hidden">
          {!selectedChat && sidebarView !== 'history' ? (
            <div className="flex-1 flex items-center justify-center relative">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-[#00E5FF]/[0.03] rounded-full blur-[120px]" />
              </div>
              <div className="text-center relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/[0.02] border border-white/[0.06] mb-5 backdrop-blur-sm">
                  <MessageCircle className="w-7 h-7 text-white/30" strokeWidth={1.5} />
                </div>
                <div className="inline-flex items-center gap-1.5 mb-2">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-white/30 font-medium">Concierge desk</span>
                </div>
                <h3 className="text-white text-xl font-light tracking-tight mb-2">
                  Select a <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">conversation</span>
                </h3>
                <p className="text-white/40 text-xs">Choose a chat from the left to begin assisting.</p>
              </div>
            </div>
          ) : sidebarView === 'history' && historySelectedChat ? (
            <>
              <div className="px-5 py-3.5 border-b border-white/[0.06] flex items-center justify-between bg-white/[0.01]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#8A2BE2]/[0.1] border border-[#8A2BE2]/25 flex items-center justify-center">
                    <User className="w-4 h-4 text-[#a673e8]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium tracking-tight">{historySelectedChat.user_name || 'Guest'}</h3>
                    <p className="text-white/40 text-[11px]">Resolved {formatTime(historySelectedChat.resolved_at)}</p>
                  </div>
                </div>
                <span className="text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full bg-[#8A2BE2]/10 text-[#a673e8] border border-[#8A2BE2]/25">Archived</span>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {historyMessages.map((msg, idx) => {
                  const isStaff = msg.sender_type === 'staff';
                  return (
                    <div key={idx} className={`flex ${isStaff ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl ${
                        isStaff ? 'bg-[#8A2BE2]/10 border border-[#8A2BE2]/25 rounded-br-sm' : 'bg-white/[0.03] border border-white/[0.06] rounded-bl-sm'
                      }`}>
                        <p className="text-white text-sm leading-relaxed">{msg.deleted ? <span className="italic text-white/30">This message was deleted</span> : msg.message}</p>
                        <p className="text-white/30 text-[10px] mt-1">
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {msg.edited && <span className="ml-1">(edited)</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            // Active Chat View
            <>
              {/* Chat Header */}
              <div className="px-5 py-3.5 border-b border-white/[0.06] flex items-center justify-between bg-white/[0.01]">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E5FF]/[0.1] to-[#8A2BE2]/[0.05] border border-white/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} />
                    </div>
                    {selectedChatInfo?.user_online && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-[#0A0A12]" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium tracking-tight" data-testid="selected-chat-name">
                        {selectedChatInfo?.user_name || `Guest ${selectedChat?.slice(0, 8)}`}
                      </h3>
                      {chatPriority[selectedChat] && (
                        <span className={`text-[10px] tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-full bg-${chatPriority[selectedChat].color}-500/15 text-${chatPriority[selectedChat].color}-300 border border-${chatPriority[selectedChat].color}-500/25`}>
                          {chatPriority[selectedChat].label}
                        </span>
                      )}
                    </div>
                    <p className="text-white/40 text-[11px] flex items-center gap-2">
                      {selectedChatInfo?.user_email || 'No email provided'}
                      {selectedChatInfo?.user_online && <span className="inline-flex items-center gap-1 text-emerald-300"><span className="w-1 h-1 rounded-full bg-emerald-400" /> Online</span>}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  {isManagement && selectedChatInfo?.claimed_by && selectedChatInfo?.claimed_by !== currentUser?.username && (
                    <Button size="sm" onClick={() => handleManagerTakeover(selectedChat)}
                      className="bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500/30 h-8 text-xs">
                      <Shield className="w-3.5 h-3.5 mr-1" /> Takeover
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setRightPanelView(rightPanelView === 'notes' ? null : 'notes')}
                    className={`h-8 w-8 p-0 ${rightPanelView === 'notes' ? 'text-violet-400 bg-violet-500/20' : 'text-slate-400'}`}
                    title="Private Notes">
                    <StickyNote className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRightPanelView(rightPanelView === 'customer-notes' ? null : 'customer-notes')}
                    className={`h-8 w-8 p-0 ${rightPanelView === 'customer-notes' ? 'text-teal-400 bg-teal-500/20' : 'text-slate-400'}`}
                    title="Shared Customer Notes" data-testid="customer-notes-btn">
                    <FileText className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowCustomerProfile(true)}
                    data-testid="open-customer-profile-btn"
                    className="h-8 px-3 rounded-full text-[#a673e8] bg-[#8A2BE2]/[0.08] hover:bg-[#8A2BE2]/15 border border-[#8A2BE2]/25"
                    title="Full Customer Profile">
                    <User className="w-3.5 h-3.5 mr-1.5" />
                    <span className="text-[10px] tracking-[0.1em] uppercase font-medium">Profile</span>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleAISummary}
                    data-testid="ai-summary-btn"
                    className="h-8 px-3 rounded-full text-[#a673e8] bg-[#8A2BE2]/[0.08] hover:bg-[#8A2BE2]/15 border border-[#8A2BE2]/25"
                    title="Nova AI conversation summary">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    <span className="text-[10px] tracking-[0.1em] uppercase font-medium">Summary</span>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowTransferModal(true)}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-white">
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={() => handleResolveChat(selectedChat)} data-testid="resolve-chat-btn"
                    className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.1em] font-medium ml-1">
                    <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Resolve
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4 relative"
                style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(0,229,255,0.025) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(138,43,226,0.02) 0%, transparent 50%)' }}>
                {chatMessages.map((msg, idx) => {
                  const isStaff = msg.sender_type === 'staff';
                  const isBot = msg.sender_type === 'bot';
                  const isUser = msg.sender_type === 'user';
                  const isEditing = editingMessageId && msg.message_id && editingMessageId === msg.message_id;
                  const canEdit = canModifyMessage(msg);
                  const canDelete = canDeleteMessage(msg);
                  const senderName = msg.sender_name || (isStaff ? 'Staff' : isBot ? 'Nova' : 'Customer');
                  const avatarUrl = msg.sender_avatar ? getImageUrl(msg.sender_avatar) : null;
                  const initials = senderName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                  
                  return (
                    <div key={msg.message_id || idx} className={`flex ${isStaff ? 'justify-end' : 'justify-start'} group`}
                      data-testid={`chat-message-${msg.message_id || idx}`}>
                      
                      {/* Left avatar for non-staff messages */}
                      {!isStaff && (
                        <div className="flex-shrink-0 mr-2.5 self-end">
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center border ${
                            isBot ? 'bg-[#8A2BE2]/[0.1] border-[#8A2BE2]/25' : 'bg-white/[0.04] border-white/[0.08]'
                          }`}>
                            {isBot ? (
                              <Bot className="w-4 h-4 text-[#a673e8]" strokeWidth={1.5} />
                            ) : (
                              <span className="text-[10px] font-medium text-white/70 tracking-wider">{initials}</span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className={`max-w-[65%] relative`}>
                        {/* Sender name label */}
                        {!isStaff && (
                          <p className="text-[10px] text-white/35 mb-1 ml-1 tracking-[0.08em] uppercase">{senderName}</p>
                        )}
                        {isStaff && (
                          <p className="text-[10px] text-white/35 mb-1 mr-1 text-right tracking-[0.08em] uppercase">{senderName}</p>
                        )}

                        {/* Delete confirmation popup */}
                        {showDeleteConfirm && msg.message_id && showDeleteConfirm === msg.message_id && (
                          <div className="absolute -top-10 right-0 z-20 bg-slate-800 border border-red-500/30 rounded-xl p-2 flex items-center gap-2 shadow-xl"
                            data-testid={`delete-confirm-${msg.message_id}`}>
                            <span className="text-red-400 text-xs">Delete?</span>
                            <Button size="sm" onClick={() => handleDeleteMessage(msg.message_id)}
                              data-testid={`confirm-delete-${msg.message_id}`}
                              className="h-6 px-2 text-[10px] bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/40">
                              Yes
                            </Button>
                            <Button size="sm" onClick={() => setShowDeleteConfirm(null)}
                              className="h-6 px-2 text-[10px] bg-slate-700 text-slate-300 hover:bg-slate-600">
                              No
                            </Button>
                          </div>
                        )}

                        {/* Edit / Delete actions for staff messages - appear on hover */}
                        {(canEdit || canDelete) && !isEditing && (
                          <div className="absolute -left-20 top-6 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-10"
                            data-testid={`message-actions-${msg.message_id}`}>
                            {canEdit && (
                              <button onClick={() => handleStartEdit(msg)}
                                data-testid={`edit-message-${msg.message_id}`}
                                className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-500/30 transition-all">
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                            {canDelete && (
                              <button onClick={() => setShowDeleteConfirm(msg.message_id)}
                                data-testid={`delete-message-${msg.message_id}`}
                                className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-red-400 hover:bg-red-500/20 hover:border-red-500/30 transition-all">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )}

                        <div className={`p-3 rounded-2xl ${
                          msg.deleted
                            ? 'bg-white/[0.02] border border-white/[0.05] rounded-br-sm'
                            : isStaff
                              ? 'bg-gradient-to-br from-[#00E5FF]/[0.12] to-[#8A2BE2]/[0.06] border border-[#00E5FF]/25 rounded-br-sm shadow-lg shadow-[#00E5FF]/[0.04]'
                              : isBot
                                ? 'bg-[#8A2BE2]/[0.06] border border-[#8A2BE2]/25 rounded-bl-sm'
                                : 'bg-white/[0.03] border border-white/[0.08] rounded-bl-sm'
                        }`}>
                          {msg.deleted ? (
                            <p className="text-slate-500 text-sm italic" data-testid={`deleted-message-${msg.message_id}`}>
                              This message was deleted
                            </p>
                          ) : isEditing ? (
                            <div className="space-y-2" data-testid={`edit-mode-${msg.message_id}`}>
                              <textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                                className="w-full bg-slate-900/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400 resize-none"
                                rows={2} autoFocus />
                              <div className="flex gap-1.5 justify-end">
                                <Button size="sm" onClick={handleCancelEdit}
                                  data-testid={`cancel-edit-${msg.message_id}`}
                                  className="h-6 px-2 text-[10px] bg-slate-700 text-slate-300 hover:bg-slate-600">
                                  Cancel
                                </Button>
                                <Button size="sm" onClick={() => handleSaveEdit(msg.message_id)}
                                  data-testid={`save-edit-${msg.message_id}`}
                                  className="h-6 px-2 text-[10px] bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/40">
                                  <Check className="w-3 h-3 mr-1" /> Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {msg.image_url && (
                                <img src={getImageUrl(msg.image_url)} alt="Shared"
                                  className="rounded-lg max-w-[250px] max-h-[180px] object-cover mb-2 cursor-pointer"
                                  onClick={() => window.open(getImageUrl(msg.image_url), '_blank')} />
                              )}
                              {msg.message && msg.message !== '📷 Image' && (
                                <p className="text-white text-sm whitespace-pre-wrap">{msg.message}</p>
                              )}
                            </>
                          )}
                        </div>
                        <div className={`flex items-center gap-1 mt-1 ${isStaff ? 'justify-end' : ''}`}>
                          <p className="text-slate-600 text-[10px]">
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {msg.edited && <span className="text-violet-400/60 ml-1">(edited)</span>}
                          </p>
                          {isStaff && !msg.deleted && <CheckCheck className="w-3 h-3 text-cyan-400" />}
                        </div>
                      </div>

                      {/* Right avatar for staff messages */}
                      {isStaff && (
                        <div className="flex-shrink-0 ml-2.5 self-end">
                          {avatarUrl && !failedAvatars[avatarUrl] ? (
                            <img src={avatarUrl} alt={senderName}
                              className="w-8 h-8 rounded-xl object-cover border border-cyan-500/25"
                              onError={() => setFailedAvatars(prev => ({ ...prev, [avatarUrl]: true }))} />
                          ) : (
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center border bg-cyan-500/15 border-cyan-500/25">
                              <span className="text-[10px] font-bold text-cyan-400">{initials}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Customer Typing Indicator */}
                {customerTyping && (
                  <div className="flex justify-start" data-testid="customer-typing-indicator"
                    style={{ animation: 'fadeInUp 0.2s ease-out' }}>
                    <div className="flex-shrink-0 mr-2.5 self-end">
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center border bg-violet-500/15 border-violet-500/25">
                        <span className="text-[10px] font-bold text-violet-400">
                          {customerTypingName?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'C'}
                        </span>
                      </div>
                    </div>
                    <div className="max-w-[65%]">
                      <p className="text-[10px] text-violet-300/70 mb-1 ml-1 tracking-[0.08em] uppercase">{customerTypingName} is typing…</p>
                      <div className="bg-white/[0.03] border border-violet-500/20 rounded-2xl rounded-bl-sm px-4 py-3">
                        <div className="flex gap-1.5">
                          <div className="w-1.5 h-1.5 bg-violet-300/60 rounded-full animate-bounce" />
                          <div className="w-1.5 h-1.5 bg-violet-300/60 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                          <div className="w-1.5 h-1.5 bg-violet-300/60 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatMessagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="px-5 py-4 border-t border-white/[0.06] bg-white/[0.01]">
                {showQuickResponses && (
                  <div className="mb-3 bg-white/[0.02] rounded-2xl p-3 border border-white/[0.06]">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/30 mb-2">Quick replies</p>
                    <div className="grid grid-cols-5 gap-2">
                      {QUICK_RESPONSES.map(qr => (
                        <button key={qr.id} onClick={() => { setChatResponse(qr.text); setShowQuickResponses(false); }}
                          data-testid={`quick-response-${qr.id}`}
                          className="p-2.5 bg-white/[0.02] border border-white/[0.05] rounded-xl text-left hover:border-[#00E5FF]/30 hover:bg-white/[0.04] transition-all">
                          <span className="text-base">{qr.icon}</span>
                          <p className="text-[#00E5FF] text-[10px] font-medium mt-1 tracking-[0.05em] uppercase">{qr.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {showEmojiPicker && (
                  <div className="mb-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
                    <div className="flex flex-wrap gap-1.5">
                      {EMOJI_LIST.map((emoji, i) => (
                        <button key={i} onClick={() => setChatResponse(prev => prev + emoji)}
                          className="text-lg hover:scale-125 transition-transform p-1 hover:bg-white/[0.05] rounded-lg">
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {showAITools && (
                  <div className="mb-3 p-3 bg-gradient-to-br from-[#8A2BE2]/[0.08] via-white/[0.02] to-[#00E5FF]/[0.06] border border-[#8A2BE2]/25 rounded-2xl backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#8A2BE2]/30 to-[#00E5FF]/20 border border-white/[0.08] flex items-center justify-center">
                          <Sparkles className="w-3 h-3 text-[#a673e8]" />
                        </div>
                        <div>
                          <span className="text-white/85 text-[11px] font-medium">Nova Enhance</span>
                          <p className="text-white/35 text-[9px] tracking-[0.1em] uppercase leading-none mt-0.5">Rewrite before you send</p>
                        </div>
                      </div>
                      {aiLoading && <span className="text-[10px] text-[#a673e8] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#a673e8] animate-pulse" />thinking…</span>}
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { key: 'professional', label: 'Professional', icon: Shield, tone: 'cyan' },
                        { key: 'friendly',     label: 'Friendly',     icon: Smile,  tone: 'amber' },
                        { key: 'concise',      label: 'Concise',      icon: Zap,    tone: 'emerald' },
                        { key: 'empathetic',   label: 'Empathetic',   icon: MessageCircle, tone: 'rose' },
                        { key: 'apologetic',   label: 'Apologise',    icon: AlertCircle,   tone: 'orange' },
                        { key: 'grammar',      label: 'Fix grammar',  icon: CheckCircle,   tone: 'violet' },
                        { key: 'expand',       label: 'Expand',       icon: ArrowUp,       tone: 'blue' },
                        { key: 'translate',    label: 'Translate',    icon: ExternalLink,  tone: 'purple' },
                        { key: 'polish',       label: 'Polish',       icon: Wand2,         tone: 'pink' },
                      ].map(a => (
                        <button key={a.key} onClick={() => handleAIImprove(a.key)} disabled={aiLoading}
                          data-testid={`ai-enhance-${a.key}`}
                          className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-[10px] font-medium tracking-[0.02em] border transition-all disabled:opacity-40 disabled:cursor-not-allowed group ${
                            a.tone === 'cyan' ? 'bg-cyan-500/[0.06] text-cyan-300 border-cyan-500/20 hover:bg-cyan-500/[0.12] hover:border-cyan-400/40' :
                            a.tone === 'amber' ? 'bg-amber-500/[0.06] text-amber-300 border-amber-500/20 hover:bg-amber-500/[0.12] hover:border-amber-400/40' :
                            a.tone === 'emerald' ? 'bg-emerald-500/[0.06] text-emerald-300 border-emerald-500/20 hover:bg-emerald-500/[0.12] hover:border-emerald-400/40' :
                            a.tone === 'rose' ? 'bg-rose-500/[0.06] text-rose-300 border-rose-500/20 hover:bg-rose-500/[0.12] hover:border-rose-400/40' :
                            a.tone === 'orange' ? 'bg-orange-500/[0.06] text-orange-300 border-orange-500/20 hover:bg-orange-500/[0.12] hover:border-orange-400/40' :
                            a.tone === 'violet' ? 'bg-violet-500/[0.06] text-violet-300 border-violet-500/20 hover:bg-violet-500/[0.12] hover:border-violet-400/40' :
                            a.tone === 'blue' ? 'bg-sky-500/[0.06] text-sky-300 border-sky-500/20 hover:bg-sky-500/[0.12] hover:border-sky-400/40' :
                            a.tone === 'purple' ? 'bg-purple-500/[0.06] text-purple-300 border-purple-500/20 hover:bg-purple-500/[0.12] hover:border-purple-400/40' :
                            'bg-pink-500/[0.06] text-pink-300 border-pink-500/20 hover:bg-pink-500/[0.12] hover:border-pink-400/40'
                          }`}>
                          <a.icon className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{a.label}</span>
                        </button>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center justify-between px-1">
                      <p className="text-[9px] text-white/25 tracking-wide">Nova preserves meaning · edits stay yours to review</p>
                      <button onClick={() => setShowAITools(false)} className="text-[9px] text-white/35 hover:text-white/70">Close</button>
                    </div>
                  </div>
                )}

                {imagePreview && (
                  <div className="mb-3 relative inline-block">
                    <img src={imagePreview} alt="Preview" className="h-16 rounded-lg" />
                    <button onClick={() => { setSelectedImage(null); setImagePreview(null); }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />
                  <div className="flex items-center bg-white/[0.03] border border-white/[0.06] rounded-full p-0.5">
                    <Button size="sm" variant="ghost" onClick={() => setShowQuickResponses(!showQuickResponses)}
                      className={`h-8 w-8 p-0 rounded-full ${showQuickResponses ? 'text-[#00E5FF] bg-[#00E5FF]/10' : 'text-white/40 hover:text-white/80'}`}>
                      <Zap className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}
                      className="h-8 w-8 p-0 rounded-full text-white/40 hover:text-white/80">
                      <ImageIcon className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className={`h-8 w-8 p-0 rounded-full ${showEmojiPicker ? 'text-violet-300 bg-violet-500/10' : 'text-white/40 hover:text-white/80'}`}>
                      <Smile className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowAITools(!showAITools)}
                      className={`h-8 w-8 p-0 rounded-full ${showAITools ? 'text-[#a673e8] bg-[#8A2BE2]/10' : 'text-white/40 hover:text-white/80'}`}>
                      <Wand2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>

                  <input type="text" value={chatResponse}
                    onChange={handleTypingInput}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendResponse()}
                    placeholder="Type your message…"
                    data-testid="staff-message-input"
                    className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-full px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#00E5FF]/30 focus:bg-white/[0.05] transition-all" />

                  <Button onClick={handleSendResponse} disabled={!chatResponse.trim() && !selectedImage}
                    data-testid="send-staff-message-btn"
                    className="bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] hover:from-[#00E5FF] hover:to-[#a673e8] text-black h-10 w-10 p-0 rounded-full shadow-lg shadow-[#00E5FF]/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Panel - Notes or Info */}
        {rightPanelView && selectedChat && (
          <div className="w-72 flex flex-col bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden">
            {rightPanelView === 'notes' && (
              <>
                <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <StickyNote className="w-4 h-4 text-violet-400" /> Internal Notes
                  </h3>
                  <Button size="sm" variant="ghost" onClick={() => setRightPanelView(null)}
                    className="h-7 w-7 p-0 text-slate-400 hover:text-white"><X className="w-4 h-4" /></Button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {privateNotes.length === 0 ? (
                    <p className="text-slate-500 text-center text-sm py-8">No notes for this chat</p>
                  ) : (
                    privateNotes.map(note => (
                      <div key={note.id} className="p-3 bg-violet-500/10 border border-violet-500/20 rounded-xl group">
                        <p className="text-white text-sm">{note.note}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-slate-500 text-[10px]">{note.author}</span>
                          <button onClick={() => handleDeleteNote(note.id)}
                            className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-3 border-t border-slate-800">
                  <div className="flex gap-2">
                    <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                      placeholder="Add a note..."
                      className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
                    <Button onClick={handleAddNote} className="bg-violet-500/20 text-violet-400 border border-violet-500/30 h-9 w-9 p-0">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}

            {rightPanelView === 'info' && selectedChatInfo && (
              <>
                <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Info className="w-4 h-4 text-cyan-400" /> Customer Info
                  </h3>
                  <Button size="sm" variant="ghost" onClick={() => setRightPanelView(null)}
                    className="h-7 w-7 p-0 text-slate-400 hover:text-white"><X className="w-4 h-4" /></Button>
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-blue-500/30 flex items-center justify-center mb-3">
                      <User className="w-8 h-8 text-cyan-400" />
                    </div>
                    <h4 className="text-white font-semibold">{selectedChatInfo?.user_name || 'Guest'}</h4>
                    <p className="text-slate-500 text-sm">{selectedChatInfo?.user_email || 'No email'}</p>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-800/50 rounded-xl p-3">
                      <p className="text-slate-500 text-xs mb-1">Session ID</p>
                      <p className="text-white text-sm font-mono">{selectedChat?.slice(0, 12)}...</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-xl p-3">
                      <p className="text-slate-500 text-xs mb-1">Status</p>
                      <Badge className={selectedChatInfo?.user_online ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'}>
                        {selectedChatInfo?.user_online ? 'Online' : 'Offline'}
                      </Badge>
                    </div>
                    <div className="bg-slate-800/50 rounded-xl p-3">
                      <p className="text-slate-500 text-xs mb-2">Priority</p>
                      <div className="flex flex-wrap gap-1">
                        {PRIORITY_OPTIONS.map(p => (
                          <button key={p.id} onClick={() => handleSetPriority(selectedChat, p)}
                            className={`px-2 py-1 rounded-lg text-[10px] font-medium border transition-all ${
                              chatPriority[selectedChat]?.id === p.id
                                ? `bg-${p.color}-500/20 text-${p.color}-400 border-${p.color}-500/30`
                                : 'bg-slate-700 text-slate-400 border-slate-600 hover:border-slate-500'
                            }`}>
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleBookmarkChat(selectedChat)}
                        className={`flex-1 h-9 ${
                          bookmarkedChats.includes(selectedChat)
                            ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                            : 'bg-slate-800 text-slate-400 border border-slate-700'
                        }`}>
                        <Bookmark className={`w-4 h-4 mr-1 ${bookmarkedChats.includes(selectedChat) ? 'fill-cyan-400' : ''}`} />
                        {bookmarkedChats.includes(selectedChat) ? 'Saved' : 'Save'}
                      </Button>
                      {isManagement && (
                        <Button size="sm" onClick={() => handleRestrictChat(selectedChat)}
                          className={`flex-1 h-9 ${
                            restrictedChats[selectedChat]
                              ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                              : 'bg-slate-800 text-slate-400 border border-slate-700'
                          }`}>
                          {restrictedChats[selectedChat] ? <Unlock className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                          {restrictedChats[selectedChat] ? 'Unlock' : 'Lock'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Customer Notes Panel */}
            {rightPanelView === 'customer-notes' && (
              <>
                <div className="p-3 border-b border-slate-800 flex items-center justify-between">
                  <h3 className="text-white text-sm font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-teal-400" /> Customer Notes
                  </h3>
                  <button onClick={() => setRightPanelView(null)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-3 space-y-3 flex-1 overflow-y-auto">
                  <p className="text-slate-500 text-[10px]">Shared notes visible to all agents</p>
                  <div className="flex gap-2">
                    <input value={newCustomerNote} onChange={e => setNewCustomerNote(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCustomerNote()}
                      placeholder="Add a note..." data-testid="customer-note-input"
                      className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500/30" />
                    <Button onClick={addCustomerNote} size="sm" className="bg-teal-500/20 text-teal-400 border border-teal-500/20 px-2">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {customerNotes.length === 0 ? (
                    <p className="text-slate-600 text-xs text-center py-4">No notes yet</p>
                  ) : customerNotes.map((n, i) => (
                    <div key={i} className="p-2.5 rounded-lg bg-slate-800/50 border border-teal-500/10">
                      <p className="text-white text-xs">{n.note}</p>
                      <p className="text-slate-600 text-[10px] mt-1">By {n.author} · {n.created_at ? new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                    </div>
                  ))}

                  {/* Previous Sessions */}
                  {customerHistory.length > 0 && (
                    <div className="pt-2 border-t border-slate-800">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium mb-2">Previous Sessions ({customerHistory.length})</p>
                      {customerHistory.map((s, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30 mb-1.5">
                          <div>
                            <p className="text-white text-[10px]">{s.user_name}</p>
                            <p className="text-slate-600 text-[9px]">{s.created_at ? new Date(s.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</p>
                          </div>
                          <Badge className={`text-[9px] ${s.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>{s.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Transfer Modal — Jewel */}
      <JewelModal
        open={showTransferModal}
        onOpenChange={setShowTransferModal}
        icon={ArrowRight}
        title="Transfer Chat"
        subtitle="Route this conversation to the right specialist"
        accent="cyan"
        size="md"
        testid="transfer-chat-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowTransferModal(false)}>Cancel</JewelButton>
          <JewelButton accent="cyan" onClick={handleTransferChat} disabled={!selectedStaff} icon={ArrowRight} data-testid="confirm-transfer-btn">Transfer</JewelButton>
        </>}
      >
        <div className="space-y-3">
          <p className="text-white/50 text-xs">Pick a teammate who's best suited to help — they'll get a notification instantly.</p>
          <div className="max-h-64 overflow-y-auto space-y-1.5 jewel-scroll">
            {staffMembers.length === 0 && <p className="text-white/40 text-sm text-center py-8">No teammates available.</p>}
            {staffMembers.map(s => {
              const online = agentStatuses.find(a => a.username === s.username);
              const active = selectedStaff === s.username;
              return (
                <button key={s.username} onClick={() => setSelectedStaff(s.username)}
                  data-testid={`transfer-staff-${s.username}`}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-xl border transition-all text-left ${
                    active
                      ? 'bg-gradient-to-r from-[#00E5FF]/[0.10] to-[#8A2BE2]/[0.06] border-[#00E5FF]/30'
                      : 'bg-white/[0.02] border-white/[0.05] hover:border-white/[0.15]'
                  }`}>
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-semibold">{(s.display_name || s.username).charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{s.display_name || s.username}</p>
                    <p className="text-white/40 text-[10px] tracking-wide capitalize">{s.role || 'Staff'}</p>
                  </div>
                  {online?.status === 'available' && <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px]"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Available</span>}
                  {online?.status === 'busy' && <span className="inline-flex items-center gap-1 text-rose-300 text-[10px]"><span className="w-1.5 h-1.5 rounded-full bg-rose-400" />Busy</span>}
                  {online?.status === 'away' && <span className="inline-flex items-center gap-1 text-violet-300 text-[10px]"><span className="w-1.5 h-1.5 rounded-full bg-violet-400" />Away</span>}
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${active ? 'bg-[#00E5FF]' : 'border border-white/15'}`}>
                    {active && <Check className="w-2.5 h-2.5 text-[#05050A]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </JewelModal>

      {/* ═══════════════════  UNIFIED LIVE CHAT SETTINGS  ═══════════════════ */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#00E5FF]" />
              Live Chat Settings
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-1 p-1 bg-white/[0.02] border border-white/[0.06] rounded-2xl w-fit mt-3">
            {[
              { id: 'general',      label: 'General',       icon: Bell },
              { id: 'availability', label: 'Availability',  icon: Power },
              { id: 'canned',       label: 'Canned Replies',icon: Bookmark },
              { id: 'managers',     label: 'Shift Managers',icon: Shield },
              { id: 'advanced',     label: 'Advanced',      icon: Zap },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setSettingsTab(t.id)}
                data-testid={`settings-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-medium transition-all ${
                  settingsTab === t.id
                    ? 'bg-gradient-to-r from-[#00E5FF]/20 to-[#8A2BE2]/20 text-white border border-white/10'
                    : 'text-white/50 hover:text-white/90 hover:bg-white/[0.03]'
                }`}
              >
                <t.icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="overflow-y-auto flex-1 pr-1 mt-4 space-y-4">
            {/* ─────── GENERAL ─────── */}
            {settingsTab === 'general' && (
              <div className="space-y-3">
                {[
                  { key: 'soundEnabled', label: 'Sound notifications', desc: 'Play a chime when new chats arrive', value: soundEnabled, onToggle: () => { const next = !soundEnabled; setSoundEnabled(next); localStorage.setItem(`livechat_sound_${currentUser?.username || 'anon'}`, next ? '1' : '0'); } },
                  { key: 'desktopNotifications', label: 'Desktop notifications', desc: 'System pop-ups for new chats even when browser is in the background', value: chatSettings.desktopNotifications, onToggle: toggleDesktopNotifs },
                  { key: 'compactList', label: 'Compact chat list', desc: 'Denser sidebar so more chats fit on screen', value: chatSettings.compactList, onToggle: () => saveSettings({ compactList: !chatSettings.compactList }) },
                ].map(row => (
                  <div key={row.key} className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                    <div className="min-w-0 pr-4">
                      <div className="text-white text-sm font-medium">{row.label}</div>
                      <div className="text-white/40 text-xs mt-0.5">{row.desc}</div>
                    </div>
                    <button onClick={row.onToggle} data-testid={`settings-toggle-${row.key}`}
                      className={`relative w-11 h-6 rounded-full transition-all ${row.value ? 'bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2]' : 'bg-white/[0.06] border border-white/10'}`}>
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${row.value ? 'left-[22px]' : 'left-0.5'}`} />
                    </button>
                  </div>
                ))}

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-white text-sm font-medium">Auto-refresh interval</div>
                    <span className="text-[#00E5FF] text-sm font-mono">{chatSettings.autoRefreshSec}s</span>
                  </div>
                  <input type="range" min="5" max="120" step="5" value={chatSettings.autoRefreshSec}
                    onChange={(e) => saveSettings({ autoRefreshSec: parseInt(e.target.value) })}
                    data-testid="settings-refresh-slider"
                    className="w-full accent-[#00E5FF]" />
                  <div className="text-white/40 text-xs mt-1">How often to check for new customer chats</div>
                </div>

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-white text-sm font-medium">SLA warning</div>
                    <span className="text-[#F97316] text-sm font-mono">{chatSettings.slaWarnMin} min</span>
                  </div>
                  <input type="range" min="1" max="15" step="1" value={chatSettings.slaWarnMin}
                    onChange={(e) => saveSettings({ slaWarnMin: parseInt(e.target.value) })}
                    data-testid="settings-sla-slider"
                    className="w-full accent-[#F97316]" />
                  <div className="text-white/40 text-xs mt-1">Show an amber warning if a chat has been unanswered longer than this</div>
                </div>
              </div>
            )}

            {/* ─────── AVAILABILITY ─────── */}
            {settingsTab === 'availability' && (
              <div className="space-y-4">
                <div className={`p-4 rounded-xl border ${chatSystemStatus.is_open ? 'bg-emerald-500/[0.06] border-emerald-500/25' : 'bg-red-500/[0.06] border-red-500/25'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/50">Current status</div>
                      <div className={`text-lg font-medium mt-0.5 ${chatSystemStatus.is_open ? 'text-emerald-300' : 'text-red-300'}`}>
                        {chatSystemStatus.is_open ? 'Live chat is OPEN' : 'Live chat is CLOSED'}
                      </div>
                      {chatSystemStatus.updated_by && (
                        <div className="text-white/40 text-xs mt-1">Last changed by {chatSystemStatus.updated_by}</div>
                      )}
                    </div>
                    <Button onClick={() => handleToggleChatStatus(!chatSystemStatus.is_open)}
                      data-testid="settings-toggle-live-status"
                      className={`rounded-full ${chatSystemStatus.is_open
                        ? 'bg-red-500/15 border border-red-500/30 text-red-200 hover:bg-red-500/25'
                        : 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25'}`}>
                      {chatSystemStatus.is_open ? <><PowerOff className="w-4 h-4 mr-2" /> Close now</> : <><Power className="w-4 h-4 mr-2" /> Open now</>}
                    </Button>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Message shown when closed</div>
                  <textarea rows={3} value={closedMessage} onChange={(e) => setClosedMessage(e.target.value)}
                    className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm resize-none focus:outline-none focus:border-[#00E5FF]/30" />
                </div>

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Auto-welcome message</div>
                  <textarea rows={2} value={chatSettings.welcomeMessage} onChange={(e) => saveSettings({ welcomeMessage: e.target.value })}
                    data-testid="settings-welcome-input"
                    className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm resize-none focus:outline-none focus:border-[#00E5FF]/30" />
                  <div className="text-white/40 text-xs mt-1">Sent automatically to every new customer chat.</div>
                </div>

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-white text-sm font-medium">Auto-open/close by business hours</div>
                      <div className="text-white/40 text-xs mt-0.5">Live chat toggles itself based on the times below</div>
                    </div>
                    <button onClick={() => saveSettings({ autoCloseAfterHours: !chatSettings.autoCloseAfterHours })}
                      data-testid="settings-toggle-business-hours"
                      className={`relative w-11 h-6 rounded-full transition-all ${chatSettings.autoCloseAfterHours ? 'bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2]' : 'bg-white/[0.06] border border-white/10'}`}>
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${chatSettings.autoCloseAfterHours ? 'left-[22px]' : 'left-0.5'}`} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Open at</div>
                      <select value={chatSettings.openHour} onChange={(e) => saveSettings({ openHour: parseInt(e.target.value) })}
                        disabled={!chatSettings.autoCloseAfterHours}
                        className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm disabled:opacity-40">
                        {Array.from({length: 24}).map((_, h) => <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Close at</div>
                      <select value={chatSettings.closeHour} onChange={(e) => saveSettings({ closeHour: parseInt(e.target.value) })}
                        disabled={!chatSettings.autoCloseAfterHours}
                        className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm disabled:opacity-40">
                        {Array.from({length: 24}).map((_, h) => <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-white text-sm font-medium">Auto-away timer</div>
                    <span className="text-[#8A2BE2] text-sm font-mono">{chatSettings.autoAwayMin} min</span>
                  </div>
                  <input type="range" min="1" max="30" step="1" value={chatSettings.autoAwayMin}
                    onChange={(e) => saveSettings({ autoAwayMin: parseInt(e.target.value) })}
                    className="w-full accent-[#8A2BE2]" />
                  <div className="text-white/40 text-xs mt-1">Mark yourself away after this many minutes of inactivity</div>
                </div>
              </div>
            )}

            {/* ─────── CANNED REPLIES ─────── */}
            {settingsTab === 'canned' && (
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-2">
                  <div className="text-xs uppercase tracking-wider text-white/50">Create a new canned reply</div>
                  <input type="text" placeholder="Title (e.g. Welcome)" value={newCanned.title}
                    onChange={(e) => setNewCanned(p => ({ ...p, title: e.target.value }))}
                    data-testid="canned-title-input"
                    className="w-full px-4 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:border-[#00E5FF]/30" />
                  <textarea rows={3} placeholder="Response content..." value={newCanned.content}
                    onChange={(e) => setNewCanned(p => ({ ...p, content: e.target.value }))}
                    data-testid="canned-content-input"
                    className="w-full px-4 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm resize-none focus:outline-none focus:border-[#00E5FF]/30" />
                  <div className="flex items-center gap-2">
                    <select value={newCanned.category} onChange={(e) => setNewCanned(p => ({ ...p, category: e.target.value }))}
                      className="flex-1 px-3 py-2 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm">
                      <option value="general">General</option><option value="greeting">Greeting</option>
                      <option value="closing">Closing</option><option value="support">Support</option><option value="info">Info</option>
                    </select>
                    <Button onClick={handleSaveCannedResponse} data-testid="save-canned-btn"
                      className="bg-gradient-to-r from-[#00E5FF]/25 to-[#8A2BE2]/25 border border-[#00E5FF]/30 text-white rounded-full">
                      <Plus className="w-4 h-4 mr-1.5" /> Save
                    </Button>
                  </div>
                </div>

                <div className="text-[10px] uppercase tracking-wider text-white/40">Existing replies ({cannedResponses.length})</div>
                {cannedResponses.length === 0 ? (
                  <div className="p-6 rounded-xl bg-white/[0.02] border border-dashed border-white/[0.08] text-center text-white/40 text-sm">
                    No canned replies yet — create your first above.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {cannedResponses.map(cr => (
                      <div key={cr.id || cr.shortcut} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] flex items-start justify-between gap-3 group">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm font-medium">{cr.title || cr.label || cr.shortcut}</span>
                            {cr.category && <Badge className="bg-[#00E5FF]/10 border-[#00E5FF]/30 text-[#00E5FF] text-[9px] uppercase">{cr.category}</Badge>}
                          </div>
                          <div className="text-white/50 text-xs mt-1 line-clamp-2">{cr.content || cr.body}</div>
                        </div>
                        <button onClick={() => handleDeleteCannedResponse(cr.id)} data-testid={`canned-delete-${cr.id || cr.shortcut}`}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-white/30 hover:text-[#F43F5E] hover:bg-[#F43F5E]/10 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ─────── SHIFT MANAGERS ─────── */}
            {settingsTab === 'managers' && (
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Assign a new shift manager</div>
                  <div className="flex gap-2">
                    <select value={selectedNewManager} onChange={e => setSelectedNewManager(e.target.value)}
                      data-testid="shift-manager-select"
                      className="flex-1 px-3 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm">
                      <option value="">Select staff member...</option>
                      {staffMembers.filter(s => !shiftManagers.some(m => m.username === s.username)).map(s => (
                        <option key={s.username} value={s.username}>{s.display_name || s.username}</option>
                      ))}
                    </select>
                    <Button onClick={handleAssignShiftManager} disabled={!selectedNewManager}
                      data-testid="assign-shift-manager-btn"
                      className="bg-gradient-to-r from-[#8A2BE2]/25 to-[#00E5FF]/25 border border-[#8A2BE2]/30 text-white rounded-full disabled:opacity-30">
                      Assign
                    </Button>
                  </div>
                  <div className="text-white/40 text-xs mt-2">Shift managers can monitor all chats, reassign agents, and oversee the team during their shift.</div>
                </div>

                <div className="text-[10px] uppercase tracking-wider text-white/40">Active shift managers ({shiftManagers.length})</div>
                {shiftManagers.length === 0 ? (
                  <div className="p-6 rounded-xl bg-white/[0.02] border border-dashed border-white/[0.08] text-center text-white/40 text-sm">
                    No shift managers assigned yet.
                  </div>
                ) : shiftManagers.map(m => (
                  <div key={m.username} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] group"
                    data-testid={`shift-manager-${m.username}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#8A2BE2]/15 border border-[#8A2BE2]/25 flex items-center justify-center">
                        <span className="text-xs font-bold text-[#a673e8]">{(m.display_name || m.username)?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{m.display_name || m.username}</p>
                        <p className="text-white/40 text-[10px]">{m.role} · Assigned by {m.assigned_by}</p>
                      </div>
                    </div>
                    <Button onClick={() => handleRemoveShiftManager(m.username)} size="sm"
                      className="bg-[#F43F5E]/10 text-[#F43F5E] border border-[#F43F5E]/25 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-sm-${m.username}`}>
                      <X className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* ─────── ADVANCED ─────── */}
            {settingsTab === 'advanced' && (
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white text-sm font-medium">Export configuration</div>
                      <div className="text-white/40 text-xs mt-0.5">Download settings, canned replies and shift managers as JSON</div>
                    </div>
                    <Button onClick={exportChatData} data-testid="settings-export-btn"
                      className="bg-white/[0.02] border border-white/10 text-white/80 hover:bg-white/[0.06] rounded-full">
                      <FileText className="w-4 h-4 mr-2" /> Export
                    </Button>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-[#F43F5E]/[0.05] border border-[#F43F5E]/25">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white text-sm font-medium">Reset settings to defaults</div>
                      <div className="text-white/40 text-xs mt-0.5">Restores refresh interval, SLA, welcome, business hours and toggles</div>
                    </div>
                    <Button onClick={() => {
                      const defaults = { desktopNotifications: false, autoRefreshSec: 30, slaWarnMin: 3, autoAwayMin: 10, welcomeMessage: 'Hi 👋 Thanks for reaching out — an agent will be with you shortly.', autoCloseAfterHours: false, openHour: 9, closeHour: 22, compactList: false };
                      setChatSettings(defaults);
                      localStorage.setItem(`livechat_settings_${currentUser?.username || 'anon'}`, JSON.stringify(defaults));
                      toast.success('Settings reset');
                    }} data-testid="settings-reset-btn"
                      className="bg-[#F43F5E]/10 border border-[#F43F5E]/30 text-[#F43F5E] hover:bg-[#F43F5E]/20 rounded-full">
                      <RefreshCw className="w-4 h-4 mr-2" /> Reset
                    </Button>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="text-xs uppercase tracking-wider text-white/50 mb-3">System health</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-2xl font-light text-white">{chatSessions.filter(s => s.status === 'active').length}</div>
                      <div className="text-white/40 text-xs">Active chats</div>
                    </div>
                    <div>
                      <div className="text-2xl font-light text-white">{agentStatuses.filter(a => a.status === 'available').length}</div>
                      <div className="text-white/40 text-xs">Agents online</div>
                    </div>
                    <div>
                      <div className="text-2xl font-light text-white">{cannedResponses.length}</div>
                      <div className="text-white/40 text-xs">Canned replies</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-3 flex justify-end border-t border-white/[0.05] mt-3">
            <Button onClick={() => setShowSettingsModal(false)} variant="outline" className="bg-white/[0.02] border-white/10 text-white/80 rounded-full">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Legacy Canned Modal — Jewel */}
      <JewelModal
        open={showCannedModal}
        onOpenChange={setShowCannedModal}
        icon={Bookmark}
        title="Save Response"
        subtitle="Turn a great reply into a reusable snippet"
        accent="violet"
        size="md"
        testid="canned-response-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowCannedModal(false)}>Cancel</JewelButton>
          <JewelButton accent="violet" onClick={handleSaveCannedResponse} icon={Bookmark}>Save reply</JewelButton>
        </>}
      >
        <div className="space-y-3">
          <JewelField label="Title" required>
            <JewelInput value={newCanned.title} onChange={(e) => setNewCanned(p => ({ ...p, title: e.target.value }))}
              placeholder="Response title" autoFocus />
          </JewelField>
          <JewelField label="Category">
            <JewelSelect value={newCanned.category} onChange={(e) => setNewCanned(p => ({ ...p, category: e.target.value }))}>
              <option value="general">General</option>
              <option value="closing">Closing</option>
              <option value="info">Info</option>
              <option value="sales">Sales</option>
              <option value="support">Support</option>
            </JewelSelect>
          </JewelField>
          <JewelField label="Content" required hint={`${newCanned.content.length} characters`}>
            <JewelTextarea value={newCanned.content} onChange={(e) => setNewCanned(p => ({ ...p, content: e.target.value }))}
              placeholder="Type the reply you'd like to reuse…" rows={5} />
          </JewelField>
        </div>
      </JewelModal>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Customer Profile Panel — slide-in from right */}
      {showCustomerProfile && selectedChat && (
        <CustomerProfilePanel
          sessionId={selectedChat}
          getAuthHeaders={getAuthHeaders}
          currentUser={currentUser}
          onClose={() => setShowCustomerProfile(false)}
          onOpenSession={(sid) => {
            setShowCustomerProfile(false);
            setSelectedChat(sid);
            toast.success('Loaded previous session');
          }}
        />
      )}

      {/* AI Summary Modal — Jewel */}
      <JewelModal
        open={showSummaryModal}
        onOpenChange={setShowSummaryModal}
        icon={Sparkles}
        title="Nova AI Summary"
        subtitle="Instant recap of this entire conversation"
        accent="violet"
        size="lg"
        testid="ai-summary-modal"
        footer={<>
          {summaryText && (
            <JewelButton variant="ghost" onClick={() => { navigator.clipboard.writeText(summaryText); toast.success('Summary copied'); }} icon={Copy}>Copy</JewelButton>
          )}
          <JewelButton accent="violet" onClick={handleAISummary} disabled={summaryLoading} loading={summaryLoading} icon={RefreshCw} data-testid="regen-summary-btn">
            {summaryLoading ? 'Thinking' : 'Regenerate'}
          </JewelButton>
        </>}
      >
        {summaryLoading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-2 border-[#8A2BE2]/20" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#a673e8] animate-spin" />
              <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-[#a673e8] animate-pulse" />
            </div>
            <p className="text-white/40 text-xs tracking-[0.2em] uppercase">Nova is reading the conversation…</p>
            <div className="flex gap-1">
              <div className="w-1.5 h-1.5 bg-[#a673e8] rounded-full animate-bounce" />
              <div className="w-1.5 h-1.5 bg-[#a673e8] rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
              <div className="w-1.5 h-1.5 bg-[#a673e8] rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
            </div>
          </div>
        ) : summaryText ? (
          <div className="space-y-3">
            <div className="relative rounded-2xl bg-gradient-to-br from-[#8A2BE2]/[0.06] via-white/[0.02] to-[#00E5FF]/[0.04] border border-white/[0.06] p-5">
              <pre className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap font-sans">{summaryText}</pre>
            </div>
            <p className="text-[10px] text-white/25 text-center">Generated by Nova · Always verify critical details</p>
          </div>
        ) : (
          <p className="text-white/40 text-sm text-center py-10">No summary yet — click Regenerate to create one.</p>
        )}
      </JewelModal>
    </div>
  );
};

export default LiveChatPageRevamped;
